import { CalendarDays, Users, TrendingUp, ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";

export const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white border border-spa-border rounded-2xl p-5">
    <div className="flex items-center justify-between mb-4">
      <p className="overline">{label}</p>
      <Icon className="w-4 h-4 text-spa-gold" strokeWidth={1.5} />
    </div>
    <p className="font-serif text-3xl text-spa-dark">{value}</p>
  </div>
);

export const PanelCard = ({ title, children }) => (
  <div className="bg-white border border-spa-border rounded-2xl p-6">
    <p className="overline mb-4">{title}</p>
    <div>{children}</div>
  </div>
);

export const BookingRow = ({ b, compact }) => (
  <div className={`${compact ? "py-3" : "p-5 sm:p-6"} flex flex-col sm:flex-row sm:items-center gap-4 ${compact ? "border-b border-spa-border/60 last:border-0" : ""}`}>
    <div className="flex-1">
      <p className="font-sans text-sm font-medium text-spa-dark">{b.name}</p>
      <p className="text-xs text-spa-gray">{b.service_name} · {b.duration}</p>
      {b.addons?.length > 0 && (
        <p data-testid={`booking-addons-${b.id}`} className="text-xs text-spa-cyan mt-0.5">
          + {b.addons.map((a) => a.name).join(", ")}
        </p>
      )}
    </div>
    {!compact && (
      <div className="text-xs text-spa-gray space-y-1 sm:max-w-xs">
        <p>{b.email} · {b.phone}</p>
        {b.address && <p>{b.address}</p>}
        {b.date && <p>Pref: {b.date}</p>}
      </div>
    )}
    <p className="font-serif text-lg text-spa-dark">${b.price}</p>
  </div>
);

export const GiftCardRow = ({ g, compact }) => {
  if (compact) {
    return (
      <div className="py-3 border-b border-spa-border/60 last:border-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-sans text-sm text-spa-dark">{g.recipient_name}</p>
            <p className="text-xs text-spa-gray">from {g.sender_name}</p>
          </div>
          <p className="font-serif text-xl text-spa-dark">${g.amount}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1">
        <p className="font-serif text-lg text-spa-dark">{g.recipient_name}</p>
        <p className="text-sm text-spa-gray">{g.recipient_email}</p>
        {g.addons?.length > 0 && (
          <p data-testid={`gift-card-addons-${g.id}`} className="text-xs text-spa-cyan mt-1">
            + {g.addons.map((a) => a.name).join(", ")}
          </p>
        )}
        {g.message && <p className="text-xs text-spa-gray mt-2 italic">&ldquo;{g.message}&rdquo;</p>}
      </div>
      <div className="text-right">
        <p className="font-serif text-2xl text-spa-dark">${g.amount}</p>
        <p className="text-xs text-spa-gray">from {g.sender_name}</p>
      </div>
    </div>
  );
};

export const OverviewTab = ({ analytics, bookings, giftCards, uniqueClients }) => (
  <div data-testid="admin-tab-overview">
    <h1 className="font-serif text-4xl text-spa-dark mb-2">Overview</h1>
    <p className="text-spa-gray mb-10">A quiet read on the practice.</p>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
      <StatCard label="Total bookings" value={analytics?.total_bookings ?? 0} icon={CalendarDays} />
      <StatCard label="Pending requests" value={analytics?.pending_bookings ?? 0} icon={ArrowUpRight} />
      <StatCard label="Unique clients" value={uniqueClients.length} icon={Users} />
      <StatCard label="Revenue (booked)" value={`$${analytics?.estimated_revenue ?? 0}`} icon={TrendingUp} />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <PanelCard title="Recent bookings">
        {bookings.slice(0, 5).map((b) => <BookingRow key={b.id} b={b} compact />)}
        {bookings.length === 0 && <p className="text-sm text-spa-gray">No bookings yet.</p>}
      </PanelCard>
      <PanelCard title="Recent gift cards">
        {giftCards.slice(0, 5).map((g) => <GiftCardRow key={g.id} g={g} compact />)}
        {giftCards.length === 0 && <p className="text-sm text-spa-gray">No gift cards yet.</p>}
      </PanelCard>
    </div>
  </div>
);

export const BookingsTab = ({ bookings }) => (
  <div data-testid="admin-tab-bookings">
    <h1 className="font-serif text-4xl text-spa-dark mb-2">Bookings</h1>
    <p className="text-spa-gray mb-8">{bookings.length} total request{bookings.length === 1 ? "" : "s"}.</p>
    <div className="bg-white border border-spa-border rounded-2xl divide-y divide-spa-border overflow-hidden">
      {bookings.length === 0 && <p className="p-8 text-sm text-spa-gray">No bookings yet.</p>}
      {bookings.map((b) => <BookingRow key={b.id} b={b} />)}
    </div>
  </div>
);

export const ClientsTab = ({ clients }) => (
  <div data-testid="admin-tab-clients">
    <h1 className="font-serif text-4xl text-spa-dark mb-2">Clients</h1>
    <p className="text-spa-gray mb-8">{clients.length} unique client{clients.length === 1 ? "" : "s"}.</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {clients.length === 0 && <p className="text-sm text-spa-gray">No clients yet.</p>}
      {clients.map((c) => (
        <div key={c.email} className="bg-white border border-spa-border rounded-2xl p-6">
          <p className="font-serif text-xl text-spa-dark">{c.name}</p>
          <div className="mt-4 space-y-2 text-sm text-spa-gray">
            <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} />{c.email}</p>
            <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} />{c.phone}</p>
            {c.address && <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />{c.address}</p>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const GiftCardsTab = ({ giftCards }) => (
  <div data-testid="admin-tab-gift-cards">
    <h1 className="font-serif text-4xl text-spa-dark mb-2">Gift Cards</h1>
    <p className="text-spa-gray mb-8">{giftCards.length} issued.</p>
    <div className="bg-white border border-spa-border rounded-2xl divide-y divide-spa-border overflow-hidden">
      {giftCards.length === 0 && <p className="p-8 text-sm text-spa-gray">No gift cards yet.</p>}
      {giftCards.map((g) => <GiftCardRow key={g.id} g={g} />)}
    </div>
  </div>
);
